import { useMemo, useState, type FocusEvent, type KeyboardEvent } from "react";
import type { BoardColumnDef, BoardLaneDef } from "./Board.types";
import {
    getShortcutAddTarget,
    type BoardColumnAddingResolver,
} from "./boardAdd";
import {
    CANT_CREATE_HERE,
    getCardEdgeAnnouncement,
} from "./boardAnnouncements";
import {
    BOARD_CARD_ATTRIBUTE,
    hasFocusLeft,
    PLACEMENT_KEY_ATTRIBUTE,
    SCROLLER_ATTRIBUTE,
} from "./boardAttributes";
import { getCellEntries, type BoardCells } from "./boardCells";
import { getExpandedIds, type BoardCollapse } from "./boardCollapse";
import {
    getNavigationTarget,
    isExpanded,
    locateCardSpot,
    resolveActiveCard,
    type BoardCardSpot,
} from "./boardNavigation";
import { BOARD_ARROW_DIRECTIONS } from "./boardDropTargets";
import { BOARD_NAVIGATION_KEYS, getCardsPerPage } from "./boardNavigationKeys";

type HintPhase = "unseen" | "shown" | "dismissed";

export interface UseBoardRovingFocusArgs<TItem> {
    cells: BoardCells<TItem>;
    columns: readonly BoardColumnDef[];
    lanes?: readonly BoardLaneDef[];
    collapse: BoardCollapse;
    getColumnAdding: BoardColumnAddingResolver<TItem>;
    /** From `useBoardFocusedKey`: the focused card, while it's on the board. */
    focusedKey: string | null;
    announce: (message: string) => void;
    /** Arrows belong to the keyboard drag while one is on. */
    isDragActive: boolean;
    /** The board creates cards (`onAddItem`), so N and Shift+N are its keys. */
    canAdd: boolean;
    /** Shift+arrow moved focus between cards; without it Shift+arrows do nothing. */
    onExtend?: (fromKey: string, toKey: string) => void;
}

/** The placement key of the card `target` is, if it's a card's root. */
export const getCardKey = (target: EventTarget) =>
    target instanceof HTMLElement && target.hasAttribute(BOARD_CARD_ATTRIBUTE)
        ? (target
              .closest(`[${PLACEMENT_KEY_ATTRIBUTE}]`)
              ?.getAttribute(PLACEMENT_KEY_ATTRIBUTE) ?? null)
        : null;

const hasCommandModifier = (event: KeyboardEvent) =>
    event.altKey || event.ctrlKey || event.metaKey;

const isSameSpot = (first: BoardCardSpot, second: BoardCardSpot | null) =>
    first.placementKey === second?.placementKey && first.index === second.index;

/**
 * The board's one Tab stop for cards, moved with the arrow keys on the board
 * model. The active card is the one focus last sat on (or arrows moved to);
 * once it's gone, the nearest card in its cell, else the first card. Arrows
 * set a focus target, which the card takes once mounted (`useBoardCardRoving`);
 * N and Shift+N create a card below or above the focused one; Shift+arrows
 * move focus and report it to `onExtend`. Also runs the hint shown the
 * first time a card gets keyboard focus.
 */
export const useBoardRovingFocus = <TItem>({
    cells,
    columns,
    lanes,
    collapse,
    getColumnAdding,
    focusedKey,
    announce,
    isDragActive,
    canAdd,
    onExtend,
}: UseBoardRovingFocusArgs<TItem>) => {
    const [lastFocused, setLastFocused] = useState<BoardCardSpot | null>(null);
    const [focusTargetKey, setFocusTargetKey] = useState<string | null>(null);
    const [hintPhase, setHintPhase] = useState<HintPhase>("unseen");
    const expanded = useMemo(
        () => getExpandedIds({ columns, lanes, collapse }),
        [columns, lanes, collapse],
    );
    const focusedSpot = useMemo(
        () => (focusedKey === null ? null : locateCardSpot(cells, focusedKey)),
        [cells, focusedKey],
    );
    if (focusedSpot !== null && !isSameSpot(focusedSpot, lastFocused)) {
        setLastFocused(focusedSpot);
    }
    // A target that left the board (deleted, filtered out, collapsed) before
    // its card mounted is dropped, so it can't take focus when it returns.
    const targetSpot = useMemo(() => {
        const spot =
            focusTargetKey === null
                ? null
                : locateCardSpot(cells, focusTargetKey);
        return spot !== null && isExpanded(expanded, spot.placement)
            ? spot
            : null;
    }, [cells, expanded, focusTargetKey]);
    if (focusTargetKey !== null && targetSpot === null) {
        setFocusTargetKey(null);
    }
    const activeSpot = useMemo(
        () =>
            resolveActiveCard({
                cells,
                expanded,
                stored: targetSpot ?? focusedSpot ?? lastFocused,
            }),
        [cells, expanded, targetSpot, focusedSpot, lastFocused],
    );
    // The fallback for a card that's gone becomes the Tab stop, so the card
    // coming back (undo, a cleared filter) doesn't take it back.
    if (
        lastFocused !== null &&
        activeSpot !== null &&
        targetSpot === null &&
        focusedSpot === null &&
        !isSameSpot(activeSpot, lastFocused)
    ) {
        setLastFocused(activeSpot);
    }

    const navigate = (from: BoardCardSpot, event: KeyboardEvent) => {
        const key = BOARD_NAVIGATION_KEYS[event.key];
        const extend =
            event.shiftKey && BOARD_ARROW_DIRECTIONS[event.key] !== undefined
                ? onExtend
                : undefined;
        if (key === undefined || (event.shiftKey && extend === undefined)) {
            return false;
        }
        const scroller =
            event.target instanceof HTMLElement
                ? event.target.closest(`[${SCROLLER_ATTRIBUTE}]`)
                : null;
        const result = getNavigationTarget({
            cells,
            expanded,
            from,
            key,
            pageSize: getCardsPerPage(scroller?.clientHeight ?? 0),
        });
        if (result.kind === "card") {
            setFocusTargetKey(result.spot.placementKey);
            extend?.(from.placementKey, result.spot.placementKey);
        } else if (result.kind === "edge") {
            announce(
                getCardEdgeAnnouncement({
                    placement: from.placement,
                    ...result.edge,
                    columns,
                    lanes,
                }),
            );
        }
        setHintPhase("dismissed");
        return true;
    };

    const createNear = (from: BoardCardSpot, event: KeyboardEvent) => {
        if (!canAdd || (event.key !== "n" && event.key !== "N")) {
            return false;
        }
        // Holding N would create a card per key repeat.
        if (event.repeat) {
            return true;
        }
        const { placement } = from;
        const column = columns.find(({ id }) => id === placement.columnId);
        const lane = lanes?.find(({ id }) => id === placement.laneId) ?? null;
        const adding =
            column === undefined ? null : getColumnAdding(column, lane);
        const target =
            adding === null
                ? null
                : getShortcutAddTarget({
                      adding,
                      placement,
                      entries: getCellEntries(cells, placement),
                      index: from.index,
                      side: event.shiftKey ? "above" : "below",
                  });
        if (adding === null || target === null) {
            announce(CANT_CREATE_HERE);
        } else {
            adding.onAdd(target);
        }
        return true;
    };

    const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (
            isDragActive ||
            event.defaultPrevented ||
            hasCommandModifier(event)
        ) {
            return;
        }
        const key = getCardKey(event.target);
        const from = key === null ? null : locateCardSpot(cells, key);
        if (
            from !== null &&
            (navigate(from, event) || createNear(from, event))
        ) {
            event.preventDefault();
        }
    };

    const onFocus = (event: FocusEvent<HTMLElement>) => {
        setFocusTargetKey(null);
        if (
            hintPhase === "unseen" &&
            getCardKey(event.target) !== null &&
            event.target.matches(":focus-visible")
        ) {
            setHintPhase("shown");
        }
    };

    const onBlur = (event: FocusEvent<HTMLElement>) => {
        if (hasFocusLeft(event)) {
            setFocusTargetKey(null);
            setHintPhase((phase) => (phase === "shown" ? "dismissed" : phase));
        }
    };

    return {
        activeSpot,
        focusTargetKey,
        isHintShown: hintPhase === "shown",
        handlers: { onKeyDown, onFocus, onBlur },
    };
};
